// ADR 23.9.26v §3 — the persisted query cache: restore, then mark stale, then
// subscribe, once per signed-in user.
//
// Imperative (`persistQueryClientRestore` / `…Subscribe`) rather than
// `PersistQueryClientProvider`, which *replaces* `QueryClientProvider` — and
// that sits outside `AuthSessionProvider`, so it could never know the `userId`
// the buster needs. This mounts inside the session provider, around the router.
//
// - `loading`: children render, nothing restores or subscribes. A public query
//   fetched in this window is not lost — hydration keeps the newer copy.
// - `signedIn` as U: `IsRestoringProvider` holds every query until the restore
//   lands; then *every* query is invalidated with `refetchType: 'none'`. That
//   is the point: `planViewQuery` is `staleTime: Infinity`, so a restored plan
//   would otherwise never refetch online and its presigned image URLs would rot
//   (3.8.26a). Online that is today's cold-load behaviour; offline the restored
//   data is simply shown.
// - U → U′ while signed in: clear, then restore under U′. U's reads never
//   render under U′.
// - `signedOut`: nothing — anonymous sessions persist nothing.
// - `lingara:signed-out`: {@link purgeOfflineData}.

import { useEffect, useRef, useState, type ReactNode } from 'react'
import { IsRestoringProvider, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { persistQueryClientRestore, persistQueryClientSubscribe } from '@tanstack/react-query-persist-client'

import { useAuthSession } from '../auth/session'
import { SIGNED_OUT_EVENT } from '../auth/tokenStore'
import { offlineDb } from './offlineDb'
import { busterFor, dehydrateOptions, MAX_AGE_MS } from './persistPolicy'
import { createPersister, QUERY_CACHE_KEY } from './persister'
import { clearSessionSnapshot } from './sessionSnapshot'
import { requestPersistentStorage } from './storagePersistence'

const persister = createPersister({ onWritten: () => requestPersistentStorage(window) })
const purgeRegistrants = new Set<() => void>()

/**
 * Register **in-memory** state elsewhere that must reset on sign-out, without
 * this file knowing its owner (W3's pending mirror and backoff timer). Stored
 * data that outlives sign-out by design — W3's queue — is not W2's to purge.
 */
export function onPurge(fn: () => void): () => void {
  purgeRegistrants.add(fn)
  return () => purgeRegistrants.delete(fn)
}

/**
 * Exactly what W2 owns: the persisted cache, the session snapshot, the
 * in-memory client — then each registrant. The `clear()` also closes a gap
 * older than this ADR: a departing learner's plans used to survive sign-out in
 * memory. Resolves when both deletes have landed.
 */
export function purgeOfflineData(client: QueryClient): Promise<void> {
  const deleted = Promise.all([offlineDb.del(QUERY_CACHE_KEY), clearSessionSnapshot()])
  client.clear()
  purgeRegistrants.forEach((fn) => fn())
  return deleted.then(() => undefined)
}

async function restoreAndSubscribe(client: QueryClient, userId: string): Promise<() => void> {
  const buster = busterFor(userId)
  try {
    // A busted or expired blob is removed by the library itself.
    await persistQueryClientRestore({ queryClient: client, persister, buster, maxAge: MAX_AGE_MS })
  } catch {
    // An unreadable blob is discarded by the library too; start empty.
  }
  await client.invalidateQueries({ refetchType: 'none' })
  const unsubscribe = persistQueryClientSubscribe({ queryClient: client, persister, buster, dehydrateOptions })
  // A write still waiting on the throttle belongs to the user being left: it
  // must not land after a switch or a purge.
  return () => {
    unsubscribe()
    persister.cancel()
  }
}

function usePurgeOnSignOut(client: QueryClient, unsubscribe: { current: (() => void) | null }): void {
  useEffect(() => {
    const onSignedOut = () => {
      unsubscribe.current?.()
      unsubscribe.current = null
      void purgeOfflineData(client)
    }
    window.addEventListener(SIGNED_OUT_EVENT, onSignedOut)
    return () => window.removeEventListener(SIGNED_OUT_EVENT, onSignedOut)
  }, [client, unsubscribe])
}

export function OfflineCache({ children }: { children: ReactNode }) {
  const { status, userId } = useAuthSession()
  const client = useQueryClient()
  const active = status === 'signedIn' ? userId : null
  const [restoredFor, setRestoredFor] = useState<string | null>(null)
  const unsubscribe = useRef<(() => void) | null>(null)
  const previous = useRef<string | null>(null)

  usePurgeOnSignOut(client, unsubscribe)
  useEffect(() => {
    if (active === null) return
    if (previous.current !== null && previous.current !== active) client.clear()
    previous.current = active
    let live = true
    void restoreAndSubscribe(client, active).then((stop) => {
      if (!live) return stop()
      unsubscribe.current = stop
      setRestoredFor(active)
    })
    return () => {
      live = false
      unsubscribe.current?.()
      unsubscribe.current = null
    }
  }, [client, active])

  const restoring = active !== null && restoredFor !== active
  return <IsRestoringProvider value={restoring}>{children}</IsRestoringProvider>
}
